import { useEffect, useState } from "react";
import { api, formatFCFA, mediaSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Eye, MousePointerClick, Heart, Plus, Pencil, Trash2, BadgeCheck, Store, Lightbulb, TrendingUp, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import DateRangePicker from "@/components/Common/DateRangePicker";

export default function MerchantDashboard() {
  const { shop, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [range, setRange] = useState({ period: "7d" });
  const [verifReq, setVerifReq] = useState(null);
  const [verifLoading, setVerifLoading] = useState(false);

  useEffect(() => {
    if (!shop) { nav("/vendre"); return; }
    const params = { ...range };
    api.get(`/meta/merchant-analytics`, { params }).then(r => setStats(r.data));
    api.get(`/products/shop/${shop.id}`).then(r => setProducts(r.data || []));
    api.get(`/shops/${shop.id}/verification-request`).then(r => setVerifReq(r.data || null)).catch(() => setVerifReq(null));
  }, [shop, range, nav]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Supprimer ce produit ? Cette action est irréversible.")) return;
    await api.delete(`/products/${id}`);
    setProducts(ps => ps.filter(p => p.id !== id));
    toast.success("Produit supprimé");
  };

  const requestVerification = async () => {
    if (!shop) return;
    setVerifLoading(true);
    try {
      const { data } = await api.post(`/shops/${shop.id}/verification-request`);
      setVerifReq(data);
      toast.success("Demande envoyée à l'administration");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    }
    setVerifLoading(false);
  };

  if (!shop) return null;

  const verifLabels = ["Non vérifié", "Profil complété", "Contact vérifié", "Boutique vérifiée"];

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-[Manrope] font-extrabold text-2xl">{shop.name}</h1>
            {shop.verified && <BadgeCheck className="h-5 w-5 text-[#0A7D3A]" />}
          </div>
          <div className="text-sm text-[#73706B] mt-0.5">
            {shop.city} · {verifLabels[shop.verification_level || 0]}
          </div>
        </div>
        <Link to="/vendre"><Button variant="outline" className="h-11 rounded-xl" data-testid="edit-shop-btn"><Store className="h-4 w-4 mr-1" />Éditer</Button></Link>
      </div>

      {/* Verification request banner */}
      {!shop.verified && (
        <VerificationBanner verifReq={verifReq} loading={verifLoading} onRequest={requestVerification} />
      )}

      <div className="mt-4">
        <DateRangePicker value={range} onChange={setRange} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
        <StatCard icon={Eye} label="Vues (période)" value={stats?.period_views ?? 0} sub={`Total cumulé : ${stats?.views || 0}`} />
        <StatCard icon={MousePointerClick} label="Clics (période)" value={stats?.period_clicks ?? 0} sub={`Total cumulé : ${stats?.clicks || 0}`} />
        <StatCard icon={Heart} label="Favoris (période)" value={stats?.period_favorites ?? 0} sub={`Total cumulé : ${stats?.favorites || 0}`} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
        <StatCard icon={TrendingUp} label="Produits actifs" value={stats?.product_count || 0} />
        <StatCard icon={BadgeCheck} label="Vérification" value={`Niveau ${shop.verification_level || 0}/3`} sub={verifLabels[shop.verification_level || 0]} />
        <StatCard icon={Eye} label="Vues boutique · 3 mois" value={stats?.shop_views_3m ?? shop.views_3m ?? 0} sub={`Total cumulé : ${stats?.shop_views || 0}`} />
      </div>

      {/* Quality recommendations (replaces raw score) */}
      {stats?.recommendations?.length > 0 && (
        <div className="mt-6 p-4 rounded-2xl bg-[#FFF8F3] border border-[#E65100]/20" data-testid="quality-recommendations">
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="h-5 w-5 text-[#E65100]" />
            <div className="font-[Manrope] font-bold text-[#1A1918]">Conseils pour améliorer votre boutique</div>
          </div>
          <ul className="space-y-1.5">
            {stats.recommendations.slice(0, 6).map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#1A1918]" data-testid={`tip-${i}`}>
                <span className="text-[#E65100] font-bold">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between mt-6">
        <h2 className="font-[Manrope] font-bold text-lg">Mes produits ({products.length})</h2>
        <Link to="/creer-produit"><Button className="h-11 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="create-product-btn"><Plus className="h-4 w-4 mr-1" />Nouveau</Button></Link>
      </div>

      <div className="mt-3 space-y-2">
        {products.map(p => (
          <div key={p.id} className="p-3 rounded-xl border border-[#E8E6E1] bg-white flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg bg-[#F3F1EC] overflow-hidden shrink-0">
              {p.images?.[0] && <img src={mediaSrc(p.images[0])} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">{p.title}</div>
              <div className="text-xs text-[#E65100] font-bold">{formatFCFA(p.price)} · Stock {p.stock}</div>
              <div className="text-[11px] text-[#73706B] mt-0.5">
                {p.views} vues · {p.clicks || 0} clics · {p.favorites_count} ♥
                {p.video && <span className="ml-2 text-[#0A7D3A] font-semibold">· Boost vidéo actif</span>}
                {(!p.images || p.images.length < 3) && <span className="ml-2 text-[#D32F2F]">· Ajoutez plus de photos</span>}
              </div>
            </div>
            <Link to={`/produit/${p.id}/modifier`}><Button size="sm" variant="outline" data-testid={`edit-product-${p.id}`} className="h-9"><Pencil className="h-4 w-4" /></Button></Link>
            <Button size="sm" variant="outline" onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`} className="h-9 text-[#D32F2F] hover:text-[#D32F2F]"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {products.length === 0 && <div className="py-10 text-center text-sm text-[#73706B]">Aucun produit. Ajoutez votre premier produit pour activer votre boutique.</div>}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-[#E8E6E1]">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider text-[#73706B] font-bold">{label}</div>
        <Icon className="h-4 w-4 text-[#73706B]" />
      </div>
      <div className="mt-1 font-[Manrope] font-extrabold text-2xl text-[#1A1918]">{value}</div>
      {sub && <div className="text-[11px] text-[#73706B]">{sub}</div>}
    </div>
  );
}

function VerificationBanner({ verifReq, loading, onRequest }) {
  // Pending request
  if (verifReq?.status === "pending") {
    return (
      <div className="mt-4 p-4 rounded-2xl bg-[#FFF3E0] border border-[#E65100]/30 flex items-center gap-3" data-testid="verif-pending">
        <Clock className="h-5 w-5 text-[#E65100] shrink-0" />
        <div className="flex-1 text-sm">
          <div className="font-bold text-[#1A1918]">Demande de vérification en cours</div>
          <div className="text-[#73706B] text-[12px]">L'équipe Forum CI vous répondra rapidement.</div>
        </div>
      </div>
    );
  }
  // Rejected request — allow re-submit
  if (verifReq?.status === "rejected") {
    return (
      <div className="mt-4 p-4 rounded-2xl bg-[#FFEAEA] border border-[#D32F2F]/30 space-y-2" data-testid="verif-rejected">
        <div className="flex items-center gap-2 font-bold text-[#1A1918]">
          <ShieldCheck className="h-5 w-5 text-[#D32F2F]" /> Demande refusée
        </div>
        {verifReq.rejection_reason && <div className="text-sm text-[#73706B]">{verifReq.rejection_reason}</div>}
        <Button onClick={onRequest} disabled={loading} className="h-10 rounded-xl bg-[#E65100] hover:bg-[#C44100]" data-testid="verif-resubmit-btn">
          {loading ? "Envoi…" : "Soumettre à nouveau"}
        </Button>
      </div>
    );
  }
  // No request yet — show CTA
  return (
    <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-[#FFF8F3] to-[#FFE4CC] border border-[#E65100]/30 flex items-center gap-3" data-testid="verif-cta">
      <ShieldCheck className="h-6 w-6 text-[#E65100] shrink-0" />
      <div className="flex-1">
        <div className="font-[Manrope] font-bold text-[#1A1918] text-sm">Augmentez la confiance des acheteurs</div>
        <div className="text-[12px] text-[#73706B]">Demandez la vérification de votre boutique pour gagner en visibilité.</div>
      </div>
      <Button onClick={onRequest} disabled={loading} className="h-10 rounded-xl bg-[#E65100] hover:bg-[#C44100] shrink-0" data-testid="verif-request-btn">
        {loading ? "Envoi…" : "Demander la vérification"}
      </Button>
    </div>
  );
}
